import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  CustomInput,
} from 'reactstrap';
import IntlCurrencyInput from 'react-intl-currency-input';
import { toast } from 'react-toastify';
import api from '~/services/api';
import Loading from '~/components/Loading';
import getDistance from '~/util/getDistance';
import DeliveryAddressPicker from '~/components/DeliveryAddressPicker';

const CURRENCY_CONFIG = {
  locale: 'pt-BR',
  formats: {
    number: {
      BRL: {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

export default function ChangeCarrierModal({
  isOpen,
  toggle,
  saleId,
  role,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState(null);
  const [operator, setOperator] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [incentive, setIncentive] = useState(0);
  const [changeAddress, setChangeAddress] = useState(false);
  const [destination, setDestination] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      try {
        const { data } = await api.get(
          `${role}/carts/${saleId}/delivery/router-options`
        );

        setOptions(data);

        const first = (data.operators || []).find(
          o => o.operator !== data.current_operator
        );

        setOperator(first ? first.operator : '');
        setVehicle('');
        setIncentive(0);
      } catch (err) {
        toast.error('Não foi possível carregar as transportadoras.');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && saleId) load();
  }, [isOpen, saleId, role]);

  const selected = (options?.operators || []).find(
    o => o.operator === operator
  );

  const vehicleLabels = options?.available_vehicles || {};
  const availableVehicles = selected?.vehicles || [];

  // Se o veículo escolhido não existe na transportadora nova, limpa.
  useEffect(() => {
    if (vehicle && !availableVehicles.includes(vehicle)) {
      setVehicle('');
    }
  }, [operator]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pré-validação de UX. A regra é garantida no backend, que bloqueia
  // com 422 mesmo se esta checagem não puder ser feita.
  const destinationWithinRadius = async () => {
    const limit = Number(options?.store?.maximum_delivery_kilometer || 0);
    const storeLat = options?.store?.lat;
    const storeLong = options?.store?.long;
    const location = destination?.location;

    if (
      !limit ||
      !storeLat ||
      !storeLong ||
      !location ||
      !window.google?.maps
    ) {
      return true;
    }

    try {
      const km = await getDistance(
        new window.google.maps.LatLng(Number(storeLat), Number(storeLong)),
        new window.google.maps.LatLng(location.lat, location.lng)
      );

      if (km > limit) {
        toast.error(
          `O endereço informado está a ${km.toFixed(
            1
          )} km da loja e ultrapassa o limite de ${limit} km. Ajuste o endereço.`,
          { autoClose: 8000 }
        );
        return false;
      }
    } catch (err) {
      // Não conseguir medir não bloqueia: o backend decide.
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!operator) {
      toast.error('Selecione uma transportadora.');
      return;
    }

    if (changeAddress && destination && !(await destinationWithinRadius())) {
      return;
    }

    if (!vehicle) {
      toast.error('Selecione o veículo.');
      return;
    }

    const minimum = Number(selected?.minimum_incentive || 0);

    if (selected?.supports_incentive && incentive > 0 && incentive < minimum) {
      toast.error(
        `${selected.label} exige incentivo mínimo de R$ ${minimum
          .toFixed(2)
          .replace('.', ',')}.`
      );
      return;
    }

    setSaving(true);

    try {
      await api.patch(`${role}/carts/${saleId}/delivery/switch-operator`, {
        operator,
        vehicle,
        incentive: selected?.supports_incentive ? incentive || 0 : 0,
        ...(changeAddress && destination
          ? {
              delivery_address: {
                ...destination.address,
                lat: destination.location?.lat,
                long: destination.location?.lng,
              },
            }
          : {}),
      });

      toast.success('Nova entrega solicitada!');
      toggle();

      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          'Não foi possível solicitar a nova entrega.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Trocar transportadora</ModalHeader>
      <ModalBody>
        {loading ? (
          <Loading />
        ) : (
          <>
            <p className="font-size-14 text-muted">
              A entrega atual será cancelada e uma nova busca será iniciada na
              transportadora escolhida. Se ninguém aceitar, o sistema segue
              automaticamente para as demais.
            </p>

            <Row className="w-100 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <FormGroup>
                  <Label>Loja</Label>
                  <Input
                    type="text"
                    value={options?.store?.name || '-'}
                    disabled
                  />
                </FormGroup>
              </Col>
              <Col lg={6} className="px-0 pl-lg-2">
                <FormGroup>
                  <Label>Transportadora atual</Label>
                  <Input
                    type="text"
                    value={options?.current_operator || '-'}
                    disabled
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="w-100 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <FormGroup>
                  <Label>Nova transportadora</Label>
                  <Input
                    type="select"
                    value={operator}
                    onChange={e => setOperator(e.target.value)}
                  >
                    <option value="">Selecione</option>
                    {(options?.operators || []).map(item => (
                      <option key={item.operator} value={item.operator}>
                        {item.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col lg={6} className="px-0 pl-lg-2">
                <FormGroup>
                  <Label>Veículo</Label>
                  <Input
                    type="select"
                    value={vehicle}
                    disabled={!operator}
                    onChange={e => setVehicle(e.target.value)}
                  >
                    <option value="">Selecione</option>
                    {availableVehicles.map(code => (
                      <option key={code} value={code}>
                        {vehicleLabels[code] || code}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row className="w-100 mx-0">
              <Col lg={6} className="px-0 pr-lg-2">
                <FormGroup>
                  <Label>Incentivo (opcional)</Label>
                  <IntlCurrencyInput
                    currency="BRL"
                    onChange={(_, value) => setIncentive(value)}
                    value={incentive}
                    className="w-100 form-control"
                    config={CURRENCY_CONFIG}
                    disabled={!selected?.supports_incentive}
                  />
                  {selected && !selected.supports_incentive && (
                    <small className="text-muted">
                      {selected.label} não aceita incentivo pela API. A entrega
                      será solicitada sem acréscimo.
                    </small>
                  )}
                  {selected && selected.supports_incentive && (
                    <small className="text-muted">
                      Valor pago ao entregador para acelerar o aceite.
                    </small>
                  )}
                  {selected?.supports_incentive &&
                    selected?.minimum_incentive > 0 && (
                      <small className="text-muted d-block">
                        Mínimo de R${' '}
                        {Number(selected.minimum_incentive)
                          .toFixed(2)
                          .replace('.', ',')}
                        .
                      </small>
                    )}
                </FormGroup>
              </Col>
            </Row>
            <Row className="w-100 mx-0 mt-2">
              <Col className="px-0">
                <CustomInput
                  type="switch"
                  id="changeAddressSwitch"
                  label="Alterar endereço de destino"
                  checked={changeAddress}
                  onChange={() => setChangeAddress(v => !v)}
                  className="text-primary"
                />
              </Col>
            </Row>

            {changeAddress && (
              <DeliveryAddressPicker
                initialAddress={options?.destination?.address}
                initialLocation={options?.destination?.location}
                onChange={setDestination}
              />
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>
          Cancelar
        </Button>
        <Button
          color="primary"
          className="new-layout-btn main"
          onClick={handleSubmit}
          disabled={saving || loading}
        >
          {saving ? 'Solicitando...' : 'Cancelar atual e solicitar'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
